import React, { useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { dashboardService } from '../../services/dashboard';
import {
  AlertBanner,
  QuickActionCard,
  TrendStatCard,
  ActivityTimeline,
  CampaignPerformanceWidget,
  UsageProgressBar,
} from '../../components/dashboard';
import { PieChart } from '../../components/charts/PieChart';
import TrafficQualityTile from '../../components/dashboard/TrafficQualityTile';
import { SkeletonDashboard } from '../../components/ui';
import { useToast } from '../../hooks/useToast';
import { useIsMobile } from '../../hooks';

/**
 * Modern Dashboard Page
 *
 * Redesigned with UX best practices:
 * - Progressive disclosure (most important first)
 * - Actionable insights with trends
 * - Clear visual hierarchy
 * - Quick actions for common tasks
 * - Smart notifications/alerts
 */
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function getTodayLabel() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export default function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const toastify = useToast();
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Check for email verification success
  useEffect(() => {
    const verified = searchParams.get('verified');
    if (verified === '1') {
      toastify.successRaw(t('toast_email_verified'));
      navigate('/dashboard', { replace: true });
    }
  }, [searchParams, navigate, t, toastify]);

  // Fetch dashboard data
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardService.getDashboard(),
    refetchInterval: 60000, // Refetch every minute
  });

  // Loading state
  if (isLoading) {
    return <SkeletonDashboard />;
  }

  // Error state
  if (error || !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{t('error_loading')}</p>
        </div>
      </div>
    );
  }

  const { overview } = data;

  // Check if user has paid (can use platform)
  const hasPaidPlan = user?.account?.can_use_platform ?? false;

  // Calculate trends (mock data - replace with real historical data)
  const requestsUsagePercentage = overview.usage.requests_percentage;
  const campaignsUsagePercentage = (overview.usage.campaigns / (overview.usage.campaigns_limit || 1)) * 100;
  const teamMembersUsagePercentage = (overview.usage.team_members / (overview.usage.team_members_limit || 1)) * 100;

  // Smart alerts based on usage
  const alerts = [];
  if (requestsUsagePercentage >= 90) {
    alerts.push({
      type: 'warning' as const,
      title: t('plan_usage.limit_approaching_title', { defaultValue: 'Request Limit Approaching' }),
      message: t('plan_usage.limit_approaching_message', {
        defaultValue: `You've used ${requestsUsagePercentage.toFixed(0)}% of your monthly request limit.`,
        percentage: requestsUsagePercentage.toFixed(0),
      }),
      actions: [
        { label: t('alerts.upgrade_plan'), href: '/subscription', variant: 'primary' as const },
        { label: t('alerts.view_usage'), href: '/statistics', variant: 'secondary' as const },
      ],
    });
  }
  if (overview.campaigns.paused > 0 && overview.campaigns.active === 0) {
    alerts.push({
      type: 'info' as const,
      title: 'All Campaigns Paused',
      message: `You have ${overview.campaigns.paused} paused campaigns. Activate them to start tracking traffic.`,
      actions: [
        { label: t('alerts.view_campaigns'), href: '/campaigns', variant: 'primary' as const },
      ],
    });
  }

  // Prepare pie chart data
  const botVsHumanData = [
    { name: t('traffic_classification.human_traffic'), value: overview.requests.humans_percentage },
    { name: t('traffic_classification.bot_traffic'), value: overview.requests.bots_percentage },
  ];

  return (
    <div className="min-h-screen p-4 md:p-8 space-y-8">
      {/* Header */}
      <div className="animate-fade-in flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {getGreeting()}, {user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'there'}
            <span className="ml-2 text-sm font-normal text-gray-400">{getTodayLabel()}</span>
          </h1>
        </div>
      </div>

      {/* Smart Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-4 animate-slide-up">
          {alerts.map((alert, index) => (
            <AlertBanner
              key={index}
              type={alert.type}
              title={alert.title}
              message={alert.message}
              actions={alert.actions}
              dismissible
            />
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className="animate-slide-up" style={{ animationDelay: '200ms' }}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="w-1 h-8 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full"></span>
          {t('quick_actions.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <QuickActionCard
            title={t('quick_actions.create_campaign')}
            icon={
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
            href="/campaigns"
            variant="primary"
            status={{ label: 'Quick Start', variant: 'info' }}
          />
          <QuickActionCard
            title={t('quick_actions.view_statistics')}
            icon={
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
            href="/statistics"
            variant="success"
          />
          <QuickActionCard
            title={t('quick_actions.manage_team')}
            icon={
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
            href="/team"
            variant="info"
          />
          <QuickActionCard
            title={t('quick_actions.upgrade_plan')}
            icon={
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
            }
            href="/subscription"
            variant="warning"
            status={!hasPaidPlan ? { label: 'Recommended', variant: 'warning' } : undefined}
          />
        </div>
      </div>

      {/* Performance Overview - Key Metrics */}
      <div className="animate-slide-up" style={{ animationDelay: '300ms' }}>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <span className="w-1 h-8 bg-gradient-to-b from-green-500 to-blue-500 rounded-full"></span>
          {t('performance.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <TrendStatCard
            title={t('campaign_overview.active_campaigns')}
            value={overview.campaigns.active}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            }
            subtitle="campaigns running"
            comparisonText="vs last week"
            variant="success"
            increaseIsPositive={true}
          />
          <TrendStatCard
            title={t('request_statistics.this_month')}
            value={overview.requests.this_month.toLocaleString()}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            subtitle="requests this month"
            comparisonText="vs last month"
            variant="primary"
            increaseIsPositive={true}
          />
          <TrendStatCard
            title="Bot Detection"
            value={`${overview.requests.bots_percentage.toFixed(1)}%`}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
            subtitle="bots detected"
            comparisonText="vs last week"
            variant="danger"
            increaseIsPositive={false}
          />
          <TrendStatCard
            title={t('campaign_overview.total_campaigns')}
            value={overview.campaigns.total}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
            subtitle="total campaigns"
            comparisonText="all time"
            variant="default"
            increaseIsPositive={true}
          />
        </div>
      </div>

      {/* Account-wide Traffic Quality */}
      <div className="animate-slide-up" style={{ animationDelay: '350ms' }}>
        <TrafficQualityTile />
      </div>

      {/* Two Column Layout: Campaigns & Bot Detection + Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-slide-up" style={{ animationDelay: '400ms' }}>
        {/* Campaign Performance Widget */}
        <CampaignPerformanceWidget
          campaigns={overview.top_campaigns.map(c => ({
            ...c,
            status: 'active' as const,
          }))}
          maxCampaigns={5}
          showViewAll
          emptyMessage="No campaigns yet. Create your first campaign to start tracking traffic."
        />

        {/* Bot Detection & Plan Usage */}
        <div className="space-y-6">
          {/* Bot vs Human Chart */}
          <div className="bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-gradient-to-b from-green-500 to-red-500 rounded-full"></span>
              {t('traffic_classification.title')}
            </h2>
            {overview.requests.total > 0 ? (
              <div>
                <PieChart
                  data={botVsHumanData}
                  title={t('traffic_classification.chart_title')}
                  colors={['#10b981', '#ef4444']}
                  height={isMobile ? 180 : 240}
                />
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-xl border-2 border-green-200">
                    <p className="text-3xl font-bold text-green-600 mb-1">
                      {overview.requests.humans_percentage.toFixed(1)}%
                    </p>
                    <p className="text-sm font-semibold text-green-700">
                      {t('traffic_classification.human_traffic')}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-xl border-2 border-red-200">
                    <p className="text-3xl font-bold text-red-600 mb-1">
                      {overview.requests.bots_percentage.toFixed(1)}%
                    </p>
                    <p className="text-sm font-semibold text-red-700">
                      {t('traffic_classification.bot_traffic')}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <svg className="w-16 h-16 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-sm">{t('traffic_classification.no_data')}</p>
                <Link to="/campaigns" className="mt-4 btn btn-primary btn-sm">
                  {t('empty_state.create_button')}
                </Link>
              </div>
            )}
          </div>

          {/* Plan Usage - Only show if user has paid plan */}
          {hasPaidPlan && (
            <div className="bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-gradient-to-b from-primary-500 to-primary-700 rounded-full"></span>
                {t('plan_usage.title')}
              </h2>
              <div className="space-y-6">
                <UsageProgressBar
                  label={t('plan_usage.monthly_requests')}
                  current={overview.usage.requests}
                  limit={overview.usage.requests_limit}
                  percentage={requestsUsagePercentage}
                />
                <UsageProgressBar
                  label={t('plan_usage.campaigns')}
                  current={overview.usage.campaigns}
                  limit={overview.usage.campaigns_limit}
                  percentage={campaignsUsagePercentage}
                />
                <UsageProgressBar
                  label={t('plan_usage.team_members')}
                  current={overview.usage.team_members}
                  limit={overview.usage.team_members_limit}
                  percentage={teamMembersUsagePercentage}
                />
                {requestsUsagePercentage < 80 && (
                  <div className="pt-4 border-t border-gray-100">
                    <Link
                      to="/subscription"
                      className="w-full btn btn-ghost text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                    >
                      Upgrade Plan
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Activity Timeline */}
      {overview.recent_activity.length > 0 && (
        <div className="animate-slide-up" style={{ animationDelay: '500ms' }}>
          <ActivityTimeline
            activities={overview.recent_activity}
            maxItems={8}
            showViewAll={false}
            emptyMessage="No recent activity"
          />
        </div>
      )}

      {/* Empty State - No Campaigns */}
      {overview.campaigns.total === 0 && (
        <div className="animate-slide-up bg-gradient-to-br from-white to-blue-50 rounded-2xl border-2 border-primary-200 p-12 text-center shadow-lg">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t('empty_state.title', { defaultValue: 'No campaigns yet' })}
            </h3>
            <p className="text-gray-600 mb-8">
              {t('empty_state.description', { defaultValue: 'Get started by creating your first filtering campaign to protect your traffic.' })}
            </p>
            <Link to="/campaigns" className="btn btn-primary btn-lg">
              {t('empty_state.create_button', { defaultValue: 'Create Your First Campaign' })}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
